import { useQuery, queryOptions, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { fetchOrThrow } from '@upright/ui/api/common/fetchers/fetch-or-throw';
import { PopulatedBlanketProductSet } from '@upright/common/model/input/blanket/blanket-product-set/types';
import { UPSERT_PLG_PRODUCT_SET_MUTATION_KEY } from '../../onboarding-wizard/select-products/query-hooks';

async function fetchEditableProductSet(companyId: string) {
  return await fetchOrThrow<PopulatedBlanketProductSet>(`/blanket/plg-company-master-product-set/${companyId}`);
}

/** Longer than a debounced save takes; a save that hangs must not leave the editor loading forever. */
const PENDING_SAVE_WAIT_CAP_MS = 15_000;

/**
 * Resolves once no product set save is pending or running. An editor closed within the autosave's
 * debounce still saves after it unmounts, so an editor opened meanwhile would otherwise load the set
 * from before that save, and its own next autosave would write the edit away.
 */
export async function waitForPendingSaves(queryClient: QueryClient): Promise<void> {
  const mutationCache = queryClient.getMutationCache();
  const saving = () => queryClient.isMutating({ mutationKey: UPSERT_PLG_PRODUCT_SET_MUTATION_KEY }) > 0;
  if (!saving()) return;
  const savesSettled = new Promise<void>(resolve => {
    const unsubscribe = mutationCache.subscribe(() => {
      if (saving()) return;
      unsubscribe();
      resolve();
    });
  });
  await Promise.race([savesSettled, new Promise<void>(resolve => setTimeout(resolve, PENDING_SAVE_WAIT_CAP_MS))]);
}

const getEditableProductSetQuery = (companyId: string, queryClient: QueryClient) =>
  queryOptions({
    queryKey: ['editable-product-set', companyId],
    queryFn: async () => {
      await waitForPendingSaves(queryClient);
      return await fetchEditableProductSet(companyId);
    },
    // Every editor starts from the server's copy (PLT-7113). The editor keeps its own state once
    // loaded and its saves do not write back to this query, so a cached copy is out of date as soon
    // as the editor saves, or the chat edits the set, and reopening the editor from it autosaved
    // that old list over the newer one. Dropped as soon as no editor is mounted; while one is,
    // nothing refetches it, so a load never replaces edits in progress.
    gcTime: 0,
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

export function usePlgCompanyEditableProductSet(companyId: string) {
  const queryClient = useQueryClient();
  return useQuery(getEditableProductSetQuery(companyId, queryClient));
}
